import { Edit, Person } from '@mui/icons-material';
import { Box, Button, IconButton, styled, TextField, Typography } from '@mui/material';
import React, { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import LlmSelect from '../components/LlmSelect';
import { AnalyticsEvent, useAnalytics } from '../components/Providers/AnalyticsProvider';
import { useAuth } from '../components/Providers/AuthProvider';
import TemperatureSelector from '../components/TemperatureSelector';
import { setLLM } from '../redux/reducers/appStatusReducer';
import { AppDispatch } from '../redux/store';
import { updateUserMetadata, updateUserPreferredName } from '../services/users';
import { ProfileAndNotesDiv } from '../theme/CustomComponents';
import { LlmEnum, llms } from '../Types/llm';
import { UserMetadata } from '../Types/user';

const Content = styled('div')({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  overflowY: 'auto',
  height: '100%',
  width: '100%',
});

const PersonalInformation = styled('div')({
  display: 'flex',
  marginBottom: '20px',
});

const ProfilePhoto = styled('img')({
  width: '80px',
  height: '80px',
  borderRadius: '50%',
});

const GeneralSettings = styled('div')({
  background: 'white',
  padding: '15px',
});

const AdvancedSettings = styled('div')({
  background: 'white',
  padding: '15px',
  paddingTop: '0px',
});

const h1 = { fontSize: '32px', color: '#F900D3', fontWeight: 'bold' };
const h2 = { marginTop: '5px', color: '#070154' };
const h4 = {
  color: '#070154',
  fontWeight: 'bold',
  fontSize: '20px',
  display: 'flex',
};

const StyledSubmitButton = styled(Button)(() => {
  return {
    height: '40px',
    width: '80px',
    marginLeft: '10px',
  };
});

const ProfileSettingsPage = (): JSX.Element => {
  const profilePhoto: string | null = localStorage.getItem('profilePhoto')
    ? localStorage.getItem('profilePhoto')
    : null;
  const { currentUser, setCurrentUser } = useAuth();
  const [isEditing, setisEditing] = useState(false);
  const nameRef = useRef<HTMLInputElement>();
  const dispatch = useDispatch<AppDispatch>();
  const analytics = useAnalytics();

  const updateLlmUserPreference = async (newLlm: LlmEnum) => {
    if (currentUser) {
      const newLlmDeployment = llms[newLlm].deploymentId;
      analytics.event(
        AnalyticsEvent.WM_NIGEL_PROFILE_SETTINGS_DEFAULT_LLM,
        {
          value: `LLM=${newLlmDeployment}`,
        },
        {
          oldLLM: currentUser.metadata.defaultLlm,
          newLLM: newLlmDeployment,
        }
      );
      const userMetadata: UserMetadata = {
        ...currentUser.metadata,
        defaultLlm: newLlmDeployment,
      };
      dispatch(setLLM(newLlm));
      await updateUserMetadata(userMetadata).then((response) => setCurrentUser(response));
    }
  };

  const updateTemperatureUserPreference = async (newTemperature: number) => {
    if (currentUser) {
      analytics.event(
        AnalyticsEvent.WM_NIGEL_PROFILE_SETTINGS_TEMP,
        {
          value: `Temp=${newTemperature}`,
        },
        {
          oldTemperature: currentUser.metadata.defaultTemperature,
          newTemperature: newTemperature,
        }
      );
      const userMetadata: UserMetadata = {
        ...currentUser.metadata,
        defaultTemperature: newTemperature,
      };
      await updateUserMetadata(userMetadata).then((response) => setCurrentUser(response));
    }
  };

  const handleUserNameEdit = useCallback(() => {
    analytics.event(AnalyticsEvent.WM_NIGEL_PROFILE_SETTINGS_NAME_EDIT);
    setisEditing(true);
  }, [analytics]);

  const handleUserNameSave = useCallback(() => {
    analytics.event(AnalyticsEvent.WM_NIGEL_PROFILE_SETTINGS_NAME_SAVE, undefined, {
      oldPreferredName: currentUser?.preferredName,
      newPreferredName: nameRef.current?.value,
    });
    updateUserPreferredName({
      preferredName: nameRef.current?.value ?? '',
    }).then((user) => {
      setCurrentUser(user);
    });
    setisEditing(false);
  }, [analytics, setCurrentUser, currentUser?.preferredName]);

  return (
    <Content>
      <ProfileAndNotesDiv>
        <PersonalInformation>
          {profilePhoto ? (
            <ProfilePhoto src={profilePhoto} alt="Profile"></ProfilePhoto>
          ) : (
            <Person
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
              }}
            />
          )}

          <Box style={{ marginLeft: '10px' }}>
            <Typography variant="h4" sx={{ mt: '12px', mb: '5px' }} style={h4}>
              {currentUser?.preferredName}
            </Typography>
            <Typography style={h2}>{currentUser?.email}</Typography>
          </Box>
        </PersonalInformation>

        <GeneralSettings>
          <Typography variant="h1" style={h1}>
            General settings
          </Typography>

          <Typography variant="h4" sx={{ mt: '10px', mb: '10px' }} style={h4}>
            Default LLM
          </Typography>

          <Typography variant="body2" sx={{ mb: '10px', color: '#070154' }}>
            The LLM selected will be used for all prompts and conversations
          </Typography>

          <LlmSelect
            value={currentUser?.metadata?.defaultLlm || ''}
            onLlmChange={updateLlmUserPreference}
          ></LlmSelect>
          <br />
          <div style={{ display: 'flex', marginTop: '10px', marginBottom: '10px' }}>
            <Typography variant="h4" style={h4}>
              Preferred Name
            </Typography>
            {!isEditing && (
              <IconButton onClick={handleUserNameEdit} style={{}}>
                <Edit />
              </IconButton>
            )}
          </div>

          {isEditing ? (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <TextField defaultValue={currentUser?.preferredName} inputRef={nameRef} />
              <StyledSubmitButton
                type="submit"
                onClick={handleUserNameSave}
                variant="contained"
                color="success"
              >
                Save
              </StyledSubmitButton>
            </div>
          ) : (
            <Typography variant="body2">{currentUser?.preferredName}</Typography>
          )}
          <br />
        </GeneralSettings>

        <AdvancedSettings>
          <Typography variant="h1" style={h1}>
            Advanced settings
          </Typography>
          <TemperatureSelector
            onTemperatureChange={updateTemperatureUserPreference}
            currentTempValue={currentUser?.metadata.defaultTemperature}
            titleBolded={true}
          />
        </AdvancedSettings>
      </ProfileAndNotesDiv>
    </Content>
  );
};

export default ProfileSettingsPage;
